// Audio exports + the AAC feeder shared with the MP4 exporter.
// - WAV: 24-bit PCM (or 16-bit dithered / 32-bit float), written chunk by chunk.
// - M4A: AAC-LC in an MP4 container (moov first) — the widely compatible audio
//   format (Apple/Windows players, phones, every NLE), unlike raw ADTS .aac.
import { Muxer, StreamTarget } from "mp4-muxer";
import { Project } from "../state/model";
import { renderChunks, RenderOpts } from "./render";
import { wavHeader, pcmBytes, BitDepth } from "./export-wav";
import { ExportSink } from "./sink";

export const AAC_RATE = 48000;
export const AAC_CHANNELS = 2;

export function aacSupported(): boolean { return typeof (globalThis as any).AudioEncoder !== "undefined"; }

/** Renders the mix in chunks and feeds it to an AudioEncoder on demand, so
 *  callers can interleave audio with video (feedUntil the next frame's time). */
export class AacFeeder {
  private encoder: AudioEncoder;
  private error: unknown = null;
  private buf: AudioBuffer | null = null; // current rendered chunk
  private bufPos = 0;                     // frames consumed from `buf`
  private fed = 0;                        // frames sent to the encoder
  private pending: AudioBuffer[] = [];
  private renderDone = false;
  private rendering: Promise<void> | null = null;
  readonly totalFrames: number;

  constructor(
    private project: Project, private buffers: Map<string, AudioBuffer>,
    private range: { start: number; end: number }, private renderOpts: RenderOpts,
    output: (chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata) => void, bitrate = 192_000,
  ) {
    this.totalFrames = Math.max(1, Math.round(range.end * AAC_RATE) - Math.round(range.start * AAC_RATE));
    this.encoder = new AudioEncoder({ output, error: (e) => { this.error ??= e; } });
    this.encoder.configure({ codec: "mp4a.40.2", sampleRate: AAC_RATE, numberOfChannels: AAC_CHANNELS, bitrate });
  }

  static async supported(bitrate = 192_000): Promise<boolean> {
    if (!aacSupported()) return false;
    try { return !!(await AudioEncoder.isConfigSupported({ codec: "mp4a.40.2", sampleRate: AAC_RATE, numberOfChannels: AAC_CHANNELS, bitrate })).supported; }
    catch { return false; }
  }

  /** Upper bound on encoded chunks (AAC = 1024 frames each, plus encoder priming). */
  get expectedChunks() { return Math.ceil(this.totalFrames / 1024) + 64; }

  private startRender(onProgress?: (f: number) => void) {
    // Render chunks one ahead of the encoder (at most ~2 chunks in memory).
    this.rendering = renderChunks(this.project, this.buffers, { ...this.renderOpts, sampleRate: AAC_RATE, start: this.range.start, end: this.range.end, chunkSeconds: 30, prerollSeconds: 3, onProgress }, async (b) => {
      this.pending.push(b);
      while (this.pending.length > 1) await new Promise((r) => setTimeout(r, 5));
    }).then(() => { this.renderDone = true; });
  }

  /** Encode audio up to `seconds` into the range (relative to range start). */
  async feedUntil(seconds: number, onProgress?: (f: number) => void) {
    if (!this.rendering) this.startRender(onProgress);
    const target = Math.min(this.totalFrames, Math.round(seconds * AAC_RATE));
    while (this.fed < target) {
      if (this.error) throw this.error;
      if (!this.buf || this.bufPos >= this.buf.length) {
        while (!this.pending.length && !this.renderDone) await new Promise((r) => setTimeout(r, 2));
        if (!this.pending.length) break; // nothing left to render
        this.buf = this.pending.shift()!; this.bufPos = 0;
      }
      const n = Math.min(4096, this.buf.length - this.bufPos, target - this.fed);
      const data = new Float32Array(n * AAC_CHANNELS);
      for (let c = 0; c < AAC_CHANNELS; c++) data.set(this.buf.getChannelData(Math.min(c, this.buf.numberOfChannels - 1)).subarray(this.bufPos, this.bufPos + n), c * n);
      const ad = new AudioData({ format: "f32-planar", sampleRate: AAC_RATE, numberOfFrames: n, numberOfChannels: AAC_CHANNELS, timestamp: Math.round((this.fed / AAC_RATE) * 1e6), data });
      this.encoder.encode(ad);
      ad.close();
      this.bufPos += n; this.fed += n;
      while (this.encoder.encodeQueueSize > 32) await new Promise((r) => setTimeout(r, 1));
    }
  }

  async finish() {
    await this.feedUntil(Infinity);
    await this.rendering;
    await this.encoder.flush();
    if (this.error) throw this.error;
    this.encoder.close();
  }
  close() { try { this.encoder.close(); } catch {} }
}

export interface AudioExportOpts {
  start: number; end: number;
  sink: ExportSink;
  onProgress?: (frac: number) => void;
  signal?: AbortSignal;
}

/** WAV mixdown, rendered and written in chunks. */
export async function exportWavMix(project: Project, buffers: Map<string, AudioBuffer>, opts: AudioExportOpts & { bitDepth?: BitDepth; sampleRate?: number; renderOpts?: RenderOpts }) {
  const sr = opts.sampleRate ?? 48000, bits = opts.bitDepth ?? 24;
  const frames = Math.max(1, Math.round(opts.end * sr) - Math.round(opts.start * sr));
  const head = wavHeader(frames, 2, sr, bits);
  opts.sink.write(head, 0);
  let pos = head.length;
  await renderChunks(project, buffers, { ...(opts.renderOpts ?? {}), sampleRate: sr, start: opts.start, end: opts.end, onProgress: opts.onProgress }, async (buf) => {
    if (opts.signal?.aborted) throw new DOMException("Export cancelled", "AbortError");
    const bytes = pcmBytes(buf, bits, 2);
    opts.sink.write(bytes, pos);
    pos += bytes.length;
    await opts.sink.drain();
  });
}

/** AAC-LC .m4a (MP4 audio, moov at the front). */
export async function exportM4a(project: Project, buffers: Map<string, AudioBuffer>, opts: AudioExportOpts & { bitrate?: number }) {
  if (!(await AacFeeder.supported(opts.bitrate))) throw new Error("This browser can't encode AAC — use Chrome or Edge, or export WAV.");
  let feeder: AacFeeder | null = null;
  const muxer = new Muxer({
    target: new StreamTarget({ onData: (d, p) => opts.sink.write(d, p), chunked: true, chunkSize: 4 * 1024 * 1024 }),
    audio: { codec: "aac", numberOfChannels: AAC_CHANNELS, sampleRate: AAC_RATE },
    fastStart: { expectedAudioChunks: Math.ceil(((opts.end - opts.start) * AAC_RATE) / 1024) + 64 },
    firstTimestampBehavior: "offset",
  });
  feeder = new AacFeeder(project, buffers, opts, {}, (c, m) => muxer.addAudioChunk(c, m), opts.bitrate);
  try {
    const step = 10; // seconds per feed step (progress + cancel granularity)
    const dur = opts.end - opts.start;
    for (let t = step; t < dur + step; t += step) {
      if (opts.signal?.aborted) throw new DOMException("Export cancelled", "AbortError");
      await feeder.feedUntil(Math.min(t, dur));
      opts.onProgress?.(Math.min(1, t / dur));
      await opts.sink.drain();
    }
    await feeder.finish();
    muxer.finalize();
  } catch (e) { feeder.close(); throw e; }
}
